<template>
  <div class="fields-manage">
    <a-alert type="info" show-icon class="fields-help" message="勾选需要展示的字段，在右侧拖动调整顺序；系统字段可修改显示名称，也可添加自定义字段。修改会同步到模板，完成后请保存模板。" />
    <a-alert v-if="loadError" type="warning" show-icon class="fields-help" :message="loadError" />
    <div class="fields-toolbar">
      <a-button v-if="loadError" @click="getFields">重新加载</a-button>
      <a-button type="link" :disabled="loading || !!loadError" @click="chooseFields">添加自定义字段</a-button>
    </div>
    <div class="fields-columns">
      <a-table
        class="fields-table"
        bordered
        size="small"
        :row-key="rowKey"
        :loading="loading"
        :pagination="{ pageSize: 10 }"
        :columns="columns"
        :data-source="fieldsList"
        :row-selection="{ selectedRowKeys: selectedRowStoreKeys, onChange: onSelectChange }"
      >
        <template #action="{ record }">
          <a-button type="link" :disabled="loading || !!loadError || record.id == null" @click="handleEdit(record)">编辑</a-button>
          <a-button v-if="!record.label" type="link" :disabled="loading || !!loadError || record.id == null" @click="handleDelete(record)">删除</a-button>
        </template>
      </a-table>
      <div class="fields-selected">
        <div class="fields-selected-title">已选字段（{{ selectedRowItems.length }}）</div>
        <a-empty v-if="!selectedRowItems.length" description="请勾选左侧字段" :image="simpleImage" />
        <div
          v-for="(item, index) in selectedRowItems"
          :key="rowKey(item)"
          class="fields-sort-item"
          draggable="true"
          @dragstart.stop="(event) => dragstart(event, index)"
          @dragover.prevent
          @drop.stop.prevent="dropField(index)"
          @dragend.stop="dragend"
        >
          <span class="fields-sort-handle">☰</span>{{ item.customerTitle || item.label || '自定义字段' }}
        </div>
      </div>
    </div>
    <a-modal
      :title="fieldsCp.title"
      v-model:visible="fieldsCp.show"
      centered
      :width="500"
      :confirm-loading="saving"
      :closable="!saving"
      :mask-closable="false"
      :keyboard="false"
      :cancel-button-props="{ disabled: saving }"
      ok-text="保存"
      cancel-text="取消"
      destroy-on-close
      @ok="onSubmit"
    >
      <a-form :model="fieldsCp.data" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item v-if="fieldsCp.data.label" label="系统字段">
          <a-input :value="fieldsCp.data.label" disabled />
        </a-form-item>
        <a-form-item label="自定义名称" :required="!fieldsCp.data.label">
          <a-input v-model:value="fieldsCp.data.customerTitle" placeholder="请输入自定义名称" />
        </a-form-item>
        <a-form-item v-if="!fieldsCp.data.label" label="自定义内容" required>
          <a-input v-model:value="fieldsCp.data.customerValue" placeholder="请输入自定义内容" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue';
import { Empty, message, Modal } from 'ant-design-vue';
import { getTraceFields, addTraceField, saveTraceField, deleteTraceField } from '@/api/qrcode';

const keyOf = (field) => field.value || field.key || field.id;
const matches = (left, right) => String(left) === String(right);
const copy = (value) => JSON.parse(JSON.stringify(value));

export default {
  props: {
    is_sys: { type: [String, Number], default: 1 },
    fields: { type: Array, default: () => [] },
    showKey: { type: Array, default: () => [] },
    selectedFields: { type: Array, default: () => [] },
  },
  emits: ['change'],
  setup(props, { emit }) {
    const fieldsList = ref([]);
    const selectedRowItems = ref([]);
    const loading = ref(false);
    const saving = ref(false);
    const loadError = ref('');
    // 表格使用统一字符串 key，兼容后端数字 ID 和旧模板中的字符串 ID。
    const rowKey = (row) => String(keyOf(row));
    const selectedRowStoreKeys = computed(() => selectedRowItems.value.map(rowKey));
    const mergeFields = (...groups) => {
      const rows = [];
      groups.forEach((group) => group.forEach((row) => {
        const index = rows.findIndex((item) => matches(keyOf(item), keyOf(row)));
        if (index < 0) rows.push(copy(row));
        else rows[index] = { ...rows[index], ...copy(row) };
      }));
      return rows;
    };
    fieldsList.value = mergeFields(props.fields, props.selectedFields);
    props.showKey.forEach((key) => {
      if (selectedRowItems.value.some((row) => matches(keyOf(row), key))) return;
      const row = fieldsList.value.find((item) => matches(keyOf(item), key) || matches(item.id, key));
      // 未能解析的旧字段也保留，避免打开弹窗时丢失配置。
      selectedRowItems.value.push(row || { value: key, customerTitle: '未识别字段（' + key + '）' });
    });
    fieldsList.value = mergeFields(fieldsList.value, selectedRowItems.value);
    const emitChange = () => emit('change', {
      keys: selectedRowItems.value.map(keyOf),
      rows: copy(selectedRowItems.value),
      type: 1,
    });
    const onSelectChange = (keys) => {
      const selected = keys.map(String);
      // 勾选其他字段时保留已有排序，新字段追加在尾部。
      const rows = selectedRowItems.value.filter((row) => selected.includes(rowKey(row)));
      selected.forEach((key) => {
        if (!rows.some((row) => rowKey(row) === key)) {
          const row = fieldsList.value.find((item) => rowKey(item) === key);
          if (row) rows.push(row);
        }
      });
      selectedRowItems.value = rows;
      emitChange();
    };
    const getFields = async () => {
      loading.value = true;
      loadError.value = '';
      try {
        const { data: result } = await getTraceFields({ is_sys: props.is_sys });
        if (result.code !== 0 || !Array.isArray(result.data)) {
          throw new Error(result.message || result.msg || '字段列表格式异常');
        }
        fieldsList.value = mergeFields(props.fields, selectedRowItems.value, result.data);
        selectedRowItems.value = selectedRowItems.value.map((row) =>
          result.data.find((item) => matches(keyOf(item), keyOf(row)) || matches(item.id, keyOf(row))) ||
          fieldsList.value.find((item) => matches(keyOf(item), keyOf(row))) || row
        );
        emitChange();
      } catch (error) {
        loadError.value = '字段列表加载失败，已保留原有配置，请重试。';
      } finally {
        loading.value = false;
      }
    };
    onMounted(getFields);

    const fieldsCp = reactive({ show: false, title: '', type: 'create', data: {} });
    const chooseFields = () => {
      fieldsCp.title = '添加自定义字段';
      fieldsCp.type = 'create';
      fieldsCp.data = { is_sys: props.is_sys, label: '', customerTitle: '', customerValue: '' };
      fieldsCp.show = true;
    };
    const handleEdit = (record) => {
      fieldsCp.title = record.label ? '修改系统字段' : '修改自定义字段';
      fieldsCp.type = 'edit';
      fieldsCp.data = { ...copy(record), is_sys: props.is_sys };
      fieldsCp.show = true;
    };
    const onSubmit = async () => {
      if (saving.value) return;
      const data = copy(fieldsCp.data);
      if (!data.label && (!String(data.customerTitle || '').trim() || !String(data.customerValue || '').trim())) {
        message.error('请输入自定义名称和内容');
        return;
      }
      saving.value = true;
      try {
        const request = fieldsCp.type === 'edit' ? saveTraceField : addTraceField;
        const { data: result } = await request(data);
        if (result.code !== 0) throw new Error(result.message || result.msg || '保存失败');
        // 先更新本地已选字段，避免后续刷新失败时仍显示旧名称。
        if (fieldsCp.type === 'edit') {
          fieldsList.value = fieldsList.value.map((row) => matches(row.id, data.id) ? { ...row, ...data } : row);
          selectedRowItems.value = selectedRowItems.value.map((row) => matches(row.id, data.id) ? { ...row, ...data } : row);
          emitChange();
        }
        fieldsCp.show = false;
        message.success(result.message || result.msg || '保存成功');
        await getFields();
      } catch (error) {
        message.error(error.message || '保存失败，请重试');
      } finally {
        saving.value = false;
      }
    };
    const handleDelete = (record) => Modal.confirm({
      centered: true,
      content: '确定删除该自定义字段？',
      onOk: async () => {
        try {
          const { data: result } = await deleteTraceField({ id: record.id });
          if (result.code !== 0) throw new Error(result.message || result.msg || '删除失败');
          fieldsList.value = fieldsList.value.filter((row) => rowKey(row) !== rowKey(record));
          selectedRowItems.value = selectedRowItems.value.filter((row) => rowKey(row) !== rowKey(record));
          emitChange();
          message.success(result.message || result.msg || '删除成功');
        } catch (error) {
          message.error(error.message || '删除失败，请重试');
          throw error;
        }
      },
    });
    const dragIndex = ref(-1);
    const dragstart = (event, index) => {
      dragIndex.value = index;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    };
    const dragend = () => { dragIndex.value = -1; };
    const dropField = (index) => {
      const start = dragIndex.value;
      dragend();
      if (start < 0 || start >= selectedRowItems.value.length || start === index) return;
      const rows = [...selectedRowItems.value];
      rows.splice(index, 0, rows.splice(start, 1)[0]);
      selectedRowItems.value = rows;
      emitChange();
    };
    return {
      fieldsList, selectedRowItems, selectedRowStoreKeys, rowKey, loading, saving, loadError,
      fieldsCp, onSelectChange, getFields, chooseFields, handleEdit, onSubmit, handleDelete,
      dragstart, dragend, dropField, simpleImage: Empty.PRESENTED_IMAGE_SIMPLE,
      columns: [
        { title: '系统字段', dataIndex: 'label', key: 'label' },
        { title: '自定义名称', dataIndex: 'customerTitle', key: 'customerTitle' },
        { title: '是否系统字段', key: 'system', customRender: ({ record }) => record.label ? '是' : '否' },
        { title: '操作', key: 'action', slots: { customRender: 'action' }, width: 150 },
      ],
    };
  },
};
</script>

<style lang="less" scoped>
.fields-help { margin-bottom: 16px; }
.fields-toolbar { display: flex; justify-content: flex-end; margin-bottom: 10px; }
.fields-columns { display: flex; gap: 16px; }
.fields-table { flex: 1; min-width: 0; }
.fields-selected { width: 220px; flex-shrink: 0; border: 1px solid #eee; padding: 12px; max-height: 560px; overflow-y: auto; }
.fields-selected-title { font-weight: bold; margin-bottom: 12px; }
.fields-sort-item { padding: 10px; margin-bottom: 8px; background: #f7fafc; border: 1px solid #eee; cursor: move; overflow-wrap: anywhere; }
.fields-sort-handle { margin-right: 8px; color: #999; }
</style>
